import type { FieldPath, FieldValues, UseFormSetError } from 'react-hook-form';
import { type ApiFieldError, isApiError } from './apiError';

export interface AppliedApiErrors {
  /** Сколько ошибок 422 легло в поля формы. */
  fieldErrors: number;
  /** Ошибки полей, которых нет в форме — показываются плашкой над формой. */
  unmatched: ApiFieldError[];
}

/**
 * Подстановка ошибок валидации backend (422, `details.fields[{field, code, message}]`)
 * в поля react-hook-form без переименования: имя поля формы = snake_case-поле контракта.
 * Ошибки неизвестных полей ставятся в `root.server` и возвращаются в `unmatched`.
 * `aliases` — поля контракта без своего контрола (`user_id` поиска) → контрол, под которым показать
 * их ошибку (`contact_email`).
 */
export function applyApiErrorsToForm<TValues extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<TValues>,
  knownFields: Iterable<string>,
  aliases: ReadonlyMap<string, string> = new Map(),
): AppliedApiErrors {
  if (!isApiError(error)) return { fieldErrors: 0, unmatched: [] };
  const known = new Set(knownFields);
  const isKnownField = (field: string): field is FieldPath<TValues> => known.has(field);
  const unmatched: ApiFieldError[] = [];
  let fieldErrors = 0;
  for (const fieldError of error.fieldErrors) {
    const target = aliases.get(fieldError.field) ?? fieldError.field;
    if (isKnownField(target)) {
      setError(
        target,
        { type: `server.${fieldError.code}`, message: fieldError.message },
        { shouldFocus: fieldErrors === 0 },
      );
      fieldErrors += 1;
    } else {
      unmatched.push(fieldError);
    }
  }
  if (unmatched.length > 0) {
    setError('root.server', {
      type: 'server',
      message: unmatched.map((item) => item.message).join(' '),
    });
  }
  return { fieldErrors, unmatched };
}
